import { useTranslation } from '@thoughtspot/i18n';
import {
    Icon,
    IconColor,
    IconID,
    IconSize,
} from '@thoughtspot/radiant-react/widgets/icon';
import { Input } from '@thoughtspot/radiant-react/widgets/input';
import {
    Horizontal,
    Vertical,
} from '@thoughtspot/radiant-react/widgets/layout';
import { TextArea } from '@thoughtspot/radiant-react/widgets/text-area';
import {
    Colors,
    Typography,
} from '@thoughtspot/radiant-react/widgets/typography';
import _ from 'lodash';
import React from 'react';
import { Key } from 'w3c-keys';
import { MAX_MESSAGE_LENGTH } from '../invite-users-modal-v2/invite-users-modal-v2.container';
import { InviteUsersContentV2Props } from '../invite-users-v2.interface';
import { UserSelectionInputTextBox } from '../user-selection-input-text-box/user-selection-input-text-box';
import styles from './invite-users-content-v2.module.scss';

export const InviteUsersContentV2: React.FC<InviteUsersContentV2Props> = ({
    emailChips,
    onRemoveUser,
    comment,
    setComment,
    onEnter,
    onBackspace,
    searchInput,
    setSearchInput,
    displayName,
    handleNameInputChange,
    validateEmail,
    chipBodyClicked,
}) => {
    const { t } = useTranslation();

    const handleTextAreaDescriptionChange = (
        event: React.ChangeEvent<HTMLTextAreaElement>,
    ) => {
        setComment(event.target.value);
    };

    const onInputChange = (value: string) => {
        if (value.slice(-1) === ',' || value.slice(-1) === ' ') {
            onEnter();
        } else {
            setSearchInput(value);
        }
    };

    const stopPropagation = (event: React.KeyboardEvent<HTMLElement>) => {
        if (event?.key === Key.Enter) {
            event.stopPropagation();
        }
    };

    const hasDisplayNameError = _.isEmpty(displayName.trim());

    const hasMessageExceedsLimit = comment.length > MAX_MESSAGE_LENGTH;

    return (
        <Vertical spacing="d" className={styles.header}>
            <Vertical spacing="f">
                <Vertical spacing="a">
                    <Horizontal spacing="a">
                        {hasDisplayNameError && (
                            <Icon
                                id={IconID.EXCLAMATION_POINT_CIRCLE}
                                size={IconSize.SMALL}
                                color={IconColor.RED}
                            />
                        )}
                        <Typography
                            variant="body-normal"
                            color={
                                hasDisplayNameError
                                    ? Colors.failure
                                    : Colors.gray
                            }
                        >
                            {t('invite.modal.displayName.header')}
                        </Typography>
                    </Horizontal>
                    <Input.Text
                        value={displayName}
                        onChange={handleNameInputChange}
                        placeholder={t('invite.modal.displayName.placeholder')}
                        ariaLabel={t('updateNameDialog.header')}
                        data-testid="display-name-edit"
                        hasError={hasDisplayNameError}
                        onEnter={stopPropagation}
                    />
                </Vertical>

                <Vertical spacing="a">
                    <Typography variant="body-normal">
                        {t('invite.modal.addEmail')}
                    </Typography>
                    <UserSelectionInputTextBox
                        searchInput={searchInput}
                        onInputChange={onInputChange}
                        onEnter={onEnter}
                        onBackspace={onBackspace}
                        removeUser={onRemoveUser}
                        userChips={emailChips}
                        onTab={onEnter}
                        validateEmail={validateEmail}
                        chipBodyClicked={chipBodyClicked}
                    />
                </Vertical>
                <Vertical spacing="a">
                    <Horizontal spacing="a">
                        {hasMessageExceedsLimit && (
                            <Icon
                                id={IconID.EXCLAMATION_POINT_CIRCLE}
                                size={IconSize.SMALL}
                                color={IconColor.RED}
                            />
                        )}
                        <Typography
                            variant="body-normal"
                            color={hasMessageExceedsLimit && Colors.failure}
                            data-testid="comment-label"
                        >
                            {t('invite.modal.message.limitLabel')}
                        </Typography>
                    </Horizontal>

                    <TextArea
                        ariaLabel="User comment"
                        value={comment}
                        name="textarea"
                        onChange={handleTextAreaDescriptionChange}
                        onKeyDown={stopPropagation}
                        hasError={hasMessageExceedsLimit}
                    />
                </Vertical>
            </Vertical>
        </Vertical>
    );
};
